import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { RealtimeChannel } from "@supabase/supabase-js";

interface PresenceState {
  onlineUsers: Set<string>;
  lastSeenMap: Map<string, Date>;
}

const HEARTBEAT_INTERVAL = 30000; // 30 seconds
const ONLINE_THRESHOLD = 60000; // Consider offline after 60 seconds of no activity
const PRESENCE_CHANNEL = 'global-presence';

export function usePresence() {
  const { user } = useAuth();
  const [presenceState, setPresenceState] = useState<PresenceState>({
    onlineUsers: new Set(),
    lastSeenMap: new Map(),
  });
  const channelRef = useRef<RealtimeChannel | null>(null);
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null);

  // Update last_seen in database
  const updateLastSeen = useCallback(async () => {
    if (!user?.id) return;
    
    await supabase
      .from('profiles')
      .update({ last_seen: new Date().toISOString() })
      .eq('user_id', user.id);
  }, [user?.id]);

  // Initialize presence tracking
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase.channel(PRESENCE_CHANNEL, {
      config: {
        presence: {
          key: user.id,
        },
      },
    });

    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        const onlineUsers = new Set<string>();
        
        Object.keys(state).forEach((key) => {
          onlineUsers.add(key);
        });

        setPresenceState((prev) => ({
          ...prev,
          onlineUsers,
        }));
      })
      .on('presence', { event: 'join' }, ({ key }) => {
        setPresenceState((prev) => {
          const newOnlineUsers = new Set(prev.onlineUsers);
          newOnlineUsers.add(key);
          return { ...prev, onlineUsers: newOnlineUsers };
        });
      })
      .on('presence', { event: 'leave' }, ({ key }) => {
        setPresenceState((prev) => {
          const newOnlineUsers = new Set(prev.onlineUsers);
          newOnlineUsers.delete(key);
          return { ...prev, onlineUsers: newOnlineUsers };
        });
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track({
            user_id: user.id,
            online_at: new Date().toISOString(),
          });
        }
      });

    channelRef.current = channel;

    // Set up heartbeat for last_seen updates
    updateLastSeen();
    heartbeatRef.current = setInterval(() => {
      updateLastSeen();
      // Re-track presence to keep connection alive
      channel.track({
        user_id: user.id,
        online_at: new Date().toISOString(),
      });
    }, HEARTBEAT_INTERVAL);

    // Handle visibility change (tab focus/blur)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateLastSeen();
        channel.track({
          user_id: user.id,
          online_at: new Date().toISOString(),
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
      }
      
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [user?.id, updateLastSeen]);

  // Check if a user is online (via realtime presence)
  const isUserOnline = useCallback((userId: string): boolean => {
    return presenceState.onlineUsers.has(userId);
  }, [presenceState.onlineUsers]);

  // Get all online user IDs
  const getOnlineUserIds = useCallback((): string[] => {
    return Array.from(presenceState.onlineUsers);
  }, [presenceState.onlineUsers]);

  return {
    isUserOnline,
    getOnlineUserIds,
    onlineCount: presenceState.onlineUsers.size,
  };
}

// Hook to check online status with fallback to last_seen
export function useUserOnlineStatus(userIds: string[]) {
  const { isUserOnline } = usePresence();
  const [lastSeenData, setLastSeenData] = useState<Record<string, Date | null>>({});

  // Fetch last_seen for users not in realtime presence
  useEffect(() => {
    if (userIds.length === 0) return;

    const fetchLastSeen = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('id, last_seen')
        .in('id', userIds);

      if (data) {
        const newLastSeen: Record<string, Date | null> = {};
        data.forEach((profile) => {
          newLastSeen[profile.id] = profile.last_seen ? new Date(profile.last_seen) : null;
        });
        setLastSeenData(newLastSeen);
      }
    };

    fetchLastSeen();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchLastSeen, 30000);
    return () => clearInterval(interval);
  }, [userIds.join(',')]);

  const getUserStatus = useCallback((userId: string): { 
    isOnline: boolean; 
    lastSeen: Date | null;
    statusText: string;
  } => {
    // First check realtime presence
    if (isUserOnline(userId)) {
      return { 
        isOnline: true, 
        lastSeen: new Date(),
        statusText: 'Online',
      };
    }

    // Fallback to last_seen
    const lastSeen = lastSeenData[userId];
    if (!lastSeen) {
      return { 
        isOnline: false, 
        lastSeen: null,
        statusText: 'Offline',
      };
    }

    const now = new Date();
    const diff = now.getTime() - lastSeen.getTime();

    // Consider online if last seen within threshold
    if (diff < ONLINE_THRESHOLD) {
      return { 
        isOnline: true, 
        lastSeen,
        statusText: 'Online',
      };
    }

    // Format last seen text
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    let statusText: string;
    if (minutes < 1) {
      statusText = 'Just now';
    } else if (minutes < 60) {
      statusText = `${minutes}m ago`;
    } else if (hours < 24) {
      statusText = `${hours}h ago`;
    } else {
      statusText = `${days}d ago`;
    }

    return { 
      isOnline: false, 
      lastSeen,
      statusText,
    };
  }, [isUserOnline, lastSeenData]);

  return { getUserStatus };
}
